import { AppStrings } from "../../utils/constants/AppStrings";

export const SPORTSBOOK_KPI_ALL_FILTER_VALUE = "all";

export type SportsbookKpiFilterOption = {
  value: string;
  label: string;
};

export type SportsbookKpiFilterValues = {
  sport?: string;
  league?: string;
  bet_type?: string[];
  money_type?: string[];
};

export type SportsbookKpiFilterOptions = {
  sports: SportsbookKpiFilterOption[];
  leagues: SportsbookKpiFilterOption[];
  betTypes: SportsbookKpiFilterOption[];
  currencyTypes: SportsbookKpiFilterOption[];
};

export type SportsbookKpiFilterAvailability = Record<
  keyof SportsbookKpiFilterValues,
  boolean
>;

export type SportsbookKpiFilterMetadata = {
  options: SportsbookKpiFilterOptions;
  selectedValues: Required<SportsbookKpiFilterValues>;
  available: SportsbookKpiFilterAvailability;
};

type FilterKey = keyof SportsbookKpiFilterValues;
type RawFilter = {
  key?: unknown;
  options?: unknown;
  selected_values?: unknown;
};

const getAllOptions = (): Record<FilterKey, SportsbookKpiFilterOption> => ({
  sport: {
    value: SPORTSBOOK_KPI_ALL_FILTER_VALUE,
    label: AppStrings.ALL_SPORTS,
  },
  league: {
    value: SPORTSBOOK_KPI_ALL_FILTER_VALUE,
    label: AppStrings.ALL_LEAGUES,
  },
  bet_type: {
    value: SPORTSBOOK_KPI_ALL_FILTER_VALUE,
    label: AppStrings.SPORTSBOOK_KPI_ALL_BET_TYPES,
  },
  money_type: {
    value: SPORTSBOOK_KPI_ALL_FILTER_VALUE,
    label: AppStrings.SPORTSBOOK_KPI_ALL_CURRENCY_TYPES,
  },
});

export const createDefaultSportsbookKpiFilterOptions =
  (): SportsbookKpiFilterOptions => {
    const all = getAllOptions();
    return {
      sports: [all.sport],
      leagues: [all.league],
      betTypes: [],
      currencyTypes: [],
    };
  };

const isRawFilter = (value: unknown): value is RawFilter =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as RawFilter).key === "string";

const findFilterArray = (
  value: unknown,
  depth = 0,
): RawFilter[] | undefined => {
  if (depth > 4 || value === null || value === undefined) return undefined;
  if (Array.isArray(value)) {
    return value.some(isRawFilter) ? value.filter(isRawFilter) : undefined;
  }
  if (typeof value !== "object") return undefined;

  const record = value as Record<string, unknown>;
  if (Array.isArray(record.filters)) {
    const filters = findFilterArray(record.filters, depth + 1);
    if (filters?.length) return filters;
  }
  for (const nestedValue of Object.values(record)) {
    const filters = findFilterArray(nestedValue, depth + 1);
    if (filters?.length) return filters;
  }
  return undefined;
};

const formatFilterLabel = (key: FilterKey, value: string): string => {
  if (key === "sport" || key === "league") return value;
  if (key === "money_type") {
    if (value === "rebet_cash") return AppStrings.CASH;
    if (value === "free_rebet_cash") return AppStrings.FREE_CASH;
  }
  return value
    .split("_")
    .map((part) =>
      part === "sgp" || part === "p2p"
        ? part.toUpperCase()
        : `${part.charAt(0).toUpperCase()}${part.slice(1)}`,
    )
    .join(" ");
};

const toOption = (
  value: unknown,
  key: FilterKey,
): SportsbookKpiFilterOption | null => {
  if (typeof value === "string") {
    const normalized = value.trim();
    return normalized
      ? { value: normalized, label: formatFilterLabel(key, normalized) }
      : null;
  }
  if (typeof value !== "object" || value === null) return null;
  const raw = value as { value?: unknown; label?: unknown };
  if (typeof raw.value !== "string" || !raw.value.trim()) return null;
  return {
    value: raw.value.trim(),
    label:
      typeof raw.label === "string" && raw.label.trim()
        ? raw.label.trim()
        : formatFilterLabel(key, raw.value.trim()),
  };
};

export const parseSportsbookKpiFilterMetadata = (
  body: unknown,
): SportsbookKpiFilterMetadata => {
  const defaults = createDefaultSportsbookKpiFilterOptions();
  const filters = findFilterArray(body);
  const selectedValues: Required<SportsbookKpiFilterValues> = {
    sport: SPORTSBOOK_KPI_ALL_FILTER_VALUE,
    league: SPORTSBOOK_KPI_ALL_FILTER_VALUE,
    bet_type: [],
    money_type: [],
  };
  const available: SportsbookKpiFilterAvailability = {
    sport: false,
    league: false,
    bet_type: false,
    money_type: false,
  };
  if (!filters) {
    return { options: defaults, selectedValues, available };
  }

  const optionMap: Record<
    FilterKey,
    keyof SportsbookKpiFilterOptions
  > = {
    sport: "sports",
    league: "leagues",
    bet_type: "betTypes",
    money_type: "currencyTypes",
  };

  for (const filter of filters) {
    const key = filter.key as FilterKey;
    const target = optionMap[key];
    if (!target) continue;
    const options = Array.isArray(filter.options)
      ? filter.options
          .map((value) => toOption(value, key))
          .filter(
            (option): option is SportsbookKpiFilterOption =>
              option !== null &&
              option.value.toLowerCase() !== SPORTSBOOK_KPI_ALL_FILTER_VALUE,
          )
      : [];
    available[key] = options.length > 0;
    defaults[target] =
      key === "sport" || key === "league"
        ? [getAllOptions()[key], ...options]
        : options;

    const selected = Array.isArray(filter.selected_values)
      ? filter.selected_values
          .map((value) => (typeof value === "string" ? value.trim() : ""))
          .filter(
            (value) =>
              Boolean(value) &&
              value.toLowerCase() !== SPORTSBOOK_KPI_ALL_FILTER_VALUE,
          )
      : [];
    if (key === "sport" || key === "league") {
      selectedValues[key] =
        selected[0] ?? SPORTSBOOK_KPI_ALL_FILTER_VALUE;
    } else {
      selectedValues[key] = selected;
    }
  }

  return { options: defaults, selectedValues, available };
};

export const parseSportsbookKpiFilterOptions = (
  body: unknown,
): SportsbookKpiFilterOptions =>
  parseSportsbookKpiFilterMetadata(body).options;

const MONEY_TYPE_PARAMS: Record<string, string> = {
  rebet_cash: "rebet_cash",
  free_rebet_cash: "rebet_free_cash",
};

const BET_TYPE_PARAMS = new Set([
  "straight",
  "group",
  "parlay",
  "sgp",
  "flex",
  "p2p",
  "round_robin",
]);

export const buildSportsbookKpiFilterParams = <
  Filters extends SportsbookKpiFilterValues,
>(
  filters: Filters,
): URLSearchParams => {
  const params = new URLSearchParams();
  (["sport", "league"] as const).forEach((key) => {
    const value = filters[key]?.trim();
    if (value && value.toLowerCase() !== SPORTSBOOK_KPI_ALL_FILTER_VALUE) {
      params.set(key, value);
    }
  });
  const betTypes = filters.bet_type
    ?.map((value) => value.trim().toLowerCase())
    .filter((value) => BET_TYPE_PARAMS.has(value));
  if (betTypes?.length) {
    params.set("bet_type", betTypes.join(","));
  }
  const moneyTypes = filters.money_type
    ?.map((value) => MONEY_TYPE_PARAMS[value.trim().toLowerCase()])
    .filter((value): value is string => Boolean(value));
  if (moneyTypes?.length) {
    params.set("money_type", moneyTypes.join(","));
  }
  return params;
};

export const buildSportsbookKpiQueryParams = (
  filters: SportsbookKpiFilterValues & {
    start_date: string;
    end_date: string;
  },
): URLSearchParams => {
  const params = buildSportsbookKpiFilterParams(filters);
  params.set("start_date", filters.start_date);
  params.set("end_date", filters.end_date);
  return params;
};
